import { hashCanonical, sha256Hex } from '../entropy/hash';
import { blockedBits } from '../entropy/estimates';
import { checkPixels, classifyRandomization, isProtected, RANDOMIZATION_TEXT, type KnownAnswer } from './randomization';
import type { Collector, Signal } from './types';
import { blocked, safe, short, signal, unavailable } from './util';

type GL = WebGLRenderingContext;

const RW = 128;
const RH = 64;

/** Renderer strings that say nothing about the GPU (masked by the browser). */
const GENERIC_RENDERERS = /^(WebKit WebGL|Mozilla|Apple GPU|Generic Renderer|Disabled)$/i;

export function isGenericRenderer(renderer: string): boolean {
  return GENERIC_RENDERERS.test(renderer.trim());
}

function createContext(kind: 'webgl' | 'webgl2', width = RW, height = RH): GL | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  try {
    return (canvas.getContext(kind, { preserveDrawingBuffer: true, antialias: true }) as GL | null) ?? null;
  } catch {
    return null;
  }
}

function release(gl: GL | null) {
  try {
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch {
    /* ignore */
  }
}

function vendorAndRenderer(gl: GL): { vendor: string; renderer: string; source: string } {
  const vendor = String(gl.getParameter(gl.VENDOR) ?? '');
  const renderer = String(gl.getParameter(gl.RENDERER) ?? '');
  // Firefox exposes the (sanitized) real renderer directly and warns if the debug extension is
  // requested, so only fall back to the extension when the plain value is the generic placeholder.
  if (/^WebKit/.test(renderer) || /^WebKit/.test(vendor)) {
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (ext) {
      return {
        vendor: String(gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) ?? vendor),
        renderer: String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) ?? renderer),
        source: 'WEBGL_debug_renderer_info',
      };
    }
  }
  return { vendor, renderer, source: 'RENDERER / VENDOR' };
}

const toPlain = (v: unknown): unknown =>
  v && typeof v === 'object' && 'length' in v ? Array.from(v as ArrayLike<number>) : (v ?? null);

function parameters(gl: GL): Record<string, unknown> {
  const names = [
    'MAX_TEXTURE_SIZE',
    'MAX_CUBE_MAP_TEXTURE_SIZE',
    'MAX_RENDERBUFFER_SIZE',
    'MAX_VIEWPORT_DIMS',
    'ALIASED_LINE_WIDTH_RANGE',
    'ALIASED_POINT_SIZE_RANGE',
    'MAX_VERTEX_ATTRIBS',
    'MAX_VERTEX_UNIFORM_VECTORS',
    'MAX_FRAGMENT_UNIFORM_VECTORS',
    'MAX_VARYING_VECTORS',
    'MAX_COMBINED_TEXTURE_IMAGE_UNITS',
    'MAX_TEXTURE_IMAGE_UNITS',
    'MAX_VERTEX_TEXTURE_IMAGE_UNITS',
    'RED_BITS',
    'GREEN_BITS',
    'BLUE_BITS',
    'ALPHA_BITS',
    'DEPTH_BITS',
    'STENCIL_BITS',
    'SHADING_LANGUAGE_VERSION',
    'VERSION',
  ] as const;
  const out: Record<string, unknown> = {};
  for (const name of names) out[name] = toPlain(gl.getParameter(gl[name]));

  const aniso =
    gl.getExtension('EXT_texture_filter_anisotropic') ??
    gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic') ??
    gl.getExtension('MOZ_EXT_texture_filter_anisotropic');
  if (aniso) out.MAX_TEXTURE_MAX_ANISOTROPY = gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT);

  const shaders = { VERTEX: gl.VERTEX_SHADER, FRAGMENT: gl.FRAGMENT_SHADER };
  const precisions = {
    LOW_FLOAT: gl.LOW_FLOAT,
    MEDIUM_FLOAT: gl.MEDIUM_FLOAT,
    HIGH_FLOAT: gl.HIGH_FLOAT,
    LOW_INT: gl.LOW_INT,
    MEDIUM_INT: gl.MEDIUM_INT,
    HIGH_INT: gl.HIGH_INT,
  };
  for (const [sName, sType] of Object.entries(shaders)) {
    for (const [pName, pType] of Object.entries(precisions)) {
      const f = gl.getShaderPrecisionFormat(sType, pType);
      out[`${sName}_${pName}`] = f ? [f.rangeMin, f.rangeMax, f.precision] : null;
    }
  }
  return out;
}

const VERTEX_SRC = `attribute vec2 p; attribute vec3 c; varying vec3 v;
void main() { v = c; gl_Position = vec4(p, 0.0, 1.0); }`;
const FRAGMENT_SRC = `precision mediump float; varying vec3 v;
void main() { gl_FragColor = vec4(v * 0.85 + 0.15 * sin(v * 37.0), 1.0); }`;

function compile(gl: GL, type: number, src: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('createShader failed');
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error('shader compile failed');
  return shader;
}

/** Draw a shaded, anti-aliased triangle and return its pixels. */
function renderTriangle(gl: GL): Uint8Array<ArrayBuffer> {
  const program = gl.createProgram();
  if (!program) throw new Error('createProgram failed');
  gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERTEX_SRC));
  gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SRC));
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('program link failed');
  gl.useProgram(program);

  // x, y, r, g, b
  const verts = new Float32Array([-0.9, -0.8, 1, 0.1, 0.3, 0.85, -0.6, 0.1, 0.9, 0.2, 0.1, 0.9, 0.2, 0.3, 1]);
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  const p = gl.getAttribLocation(program, 'p');
  const c = gl.getAttribLocation(program, 'c');
  gl.enableVertexAttribArray(p);
  gl.vertexAttribPointer(p, 2, gl.FLOAT, false, 20, 0);
  gl.enableVertexAttribArray(c);
  gl.vertexAttribPointer(c, 3, gl.FLOAT, false, 20, 8);

  gl.viewport(0, 0, RW, RH);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.drawArrays(gl.TRIANGLES, 0, 3);
  const pixels = new Uint8Array(RW * RH * 4);
  gl.readPixels(0, 0, RW, RH, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
  return pixels;
}

/** Clear to a color that converts exactly to 8-bit (0.2→51, 0.4→102, 0.6→153) and read it back. */
function knownAnswer(gl: GL): KnownAnswer {
  gl.viewport(0, 0, RW, RH);
  gl.clearColor(0.2, 0.4, 0.6, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  const size = 16;
  const pixels = new Uint8Array(size * size * 4);
  gl.readPixels(0, 0, size, size, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
  return checkPixels(pixels, size, () => [51, 102, 153, 255]);
}

export const webglCollector: Collector = {
  id: 'webgl',
  title: 'WebGL',
  description: 'Your graphics card and driver, as seen through the 3D graphics API: its name, limits, and how it renders.',
  async collect() {
    const gl1 = createContext('webgl');
    const gl2 = createContext('webgl2');
    const gl = gl1 ?? gl2;
    const support = { webgl: !!gl1, webgl2: !!gl2 };
    release(gl2 && gl1 ? gl2 : null);

    const supportSignal = signal('webgl.support', 'WebGL support', support, {
      display: `WebGL 1: ${support.webgl ? 'yes' : 'no'} · WebGL 2: ${support.webgl2 ? 'yes' : 'no'}`,
    });
    if (!gl) {
      const note = 'WebGL is disabled, blocked, or unsupported';
      return [
        { ...supportSignal, available: false, bits: blockedBits('webgl.support'), note },
        blocked('webgl.vendor', 'GPU vendor', note),
        blocked('webgl.renderer', 'GPU renderer', note),
        blocked('webgl.extensions', 'Supported extensions', note),
        blocked('webgl.params', 'Capability parameters', note),
        blocked('webgl.render', 'Rendering hash', note),
        signal('webgl.randomization', 'Randomization check', 'unavailable', { display: RANDOMIZATION_TEXT.unavailable }),
      ];
    }

    try {
      const ids = await safe('webgl.renderer', 'GPU renderer', () => {
        const r = vendorAndRenderer(gl);
        return signal('webgl.renderer', 'GPU renderer', r.renderer, { note: `via ${r.source}` });
      });
      const vendorProbe = safe('webgl.vendor', 'GPU vendor', () => signal('webgl.vendor', 'GPU vendor', vendorAndRenderer(gl).vendor));

      if (ids.available && isGenericRenderer(String(ids.value))) {
        ids.bits = 1;
        ids.note = 'Generic value: your browser masks the real GPU name.';
      }

      const extensions = safe('webgl.extensions', 'Supported extensions', async () => {
        const list = [...(gl.getSupportedExtensions() ?? [])].sort();
        const hash = await sha256Hex(list.join(','));
        return signal('webgl.extensions', 'Supported extensions', { count: list.length, sha256: hash, list }, {
          display: `${list.length} extensions · ${short(hash)}…\n${list.join(', ')}`,
        });
      });

      const params = safe('webgl.params', 'Capability parameters', async () => {
        const p = parameters(gl);
        const hash = await hashCanonical(p);
        const summary = `max texture ${String(p.MAX_TEXTURE_SIZE)} · viewport ${JSON.stringify(p.MAX_VIEWPORT_DIMS)} · line width ${JSON.stringify(p.ALIASED_LINE_WIDTH_RANGE)}`;
        return signal('webgl.params', 'Capability parameters', { sha256: hash, params: p }, {
          display: `${short(hash)}… · ${summary}\n${JSON.stringify(p)}`,
        });
      });

      let ka: KnownAnswer;
      try {
        ka = knownAnswer(gl);
      } catch {
        ka = 'unavailable';
      }
      let first: string | null = null;
      let second: string | null = null;
      let renderError = '';
      try {
        first = await sha256Hex(renderTriangle(gl));
        second = await sha256Hex(renderTriangle(gl));
      } catch (err) {
        renderError = err instanceof Error ? err.message : String(err);
      }
      const state = classifyRandomization({ first, second, knownAnswer: ka });

      let render: Signal;
      if (first === null) render = unavailable('webgl.render', 'Rendering hash', renderError || 'Rendering failed');
      else if (state === 'blocked')
        render = { ...signal('webgl.render', 'Rendering hash', first, { display: `${short(first)}…` }), available: false, bits: blockedBits('webgl.render'), note: 'Readback blocked.' };
      else if (isProtected(state))
        render = signal('webgl.render', 'Rendering hash', first, {
          display: `${short(first)}…`,
          bits: 0,
          randomized: true,
          note: 'Randomized by your browser, so it is not a stable identifier.',
        });
      else render = signal('webgl.render', 'Rendering hash', first, { display: `${short(first)}… (SHA-256 of ${RW}×${RH} pixels)` });

      const randomization = signal('webgl.randomization', 'Randomization check', { state, knownAnswer: ka, repeatMatched: first === second }, {
        display: RANDOMIZATION_TEXT[state],
      });

      return [supportSignal, await vendorProbe, ids, await extensions, await params, render, randomization];
    } finally {
      release(gl);
    }
  },
};
